/**
 * ScheduledRunCard - Display a scheduled running session
 */

import { motion } from 'framer-motion';
import type { ScheduledRun } from '@/types/runPlanner';
import { useRunPlannerStore } from '@/stores';
import { cn } from '@/utils/cn';

interface ScheduledRunCardProps {
  run: ScheduledRun;
}

export function ScheduledRunCard({ run }: ScheduledRunCardProps) {
  const cancelRun = useRunPlannerStore((state) => state.cancelRun);
  const startRun = useRunPlannerStore((state) => state.startRun);
  const distanceKm = (run.route.distance / 1000).toFixed(1);

  const formatDate = (date: Date) => {
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (d.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const confirmedCount = run.participants.filter(p => p.status === 'confirmed').length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-xl bg-white/5 border border-white/10"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-white">{run.route.name}</h3>
          <p className="text-sm text-gray-400">{distanceKm} km run</p>
        </div>
        <div className={cn(
          'px-2 py-1 rounded text-xs font-medium',
          run.status === 'scheduled' && 'bg-cyan-500/20 text-cyan-400',
          run.status === 'in_progress' && 'bg-emerald-500/20 text-emerald-400',
          run.status === 'completed' && 'bg-gray-500/20 text-gray-400',
          run.status === 'cancelled' && 'bg-red-500/20 text-red-400'
        )}>
          {run.status.replace('_', ' ').toUpperCase()}
        </div>
      </div>

      {/* Date & Time */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-white/5 rounded-lg">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-cyan-400" />
          <span className="text-white">{formatDate(run.scheduledTime)}</span>
        </div>
        <div className="flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-cyan-400" />
          <span className="text-white">{formatTime(run.scheduledTime)}</span>
        </div>
      </div>

      {/* Route Scores */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center p-2 bg-white/5 rounded-lg">
          <div className="text-lg font-bold text-emerald-400">{run.route.scores.airQuality}</div>
          <div className="text-[10px] text-gray-400">Air Quality</div>
        </div>
        <div className="text-center p-2 bg-white/5 rounded-lg">
          <div className="text-lg font-bold text-blue-400">{run.route.scores.safety}</div>
          <div className="text-[10px] text-gray-400">Safety</div>
        </div>
        <div className="text-center p-2 bg-white/5 rounded-lg">
          <div className="text-lg font-bold text-violet-400">+{run.route.scores.neuralIndexImpact}%</div>
          <div className="text-[10px] text-gray-400">Brain Boost</div>
        </div>
      </div>

      {/* Participants */}
      {run.participants.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-gray-400 mb-2">
            Participants ({confirmedCount}/{run.participants.length} confirmed)
          </div>
          <div className="flex items-center gap-2">
            {run.participants.map((participant) => (
              <div
                key={participant.id}
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2',
                  participant.status === 'confirmed' && 'bg-emerald-500/20 border-emerald-500 text-emerald-400',
                  participant.status === 'invited' && 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
                  participant.status === 'declined' && 'bg-red-500/20 border-red-500 text-red-400',
                  participant.status === 'maybe' && 'bg-gray-500/20 border-gray-500 text-gray-400'
                )}
                title={`${participant.name} - ${participant.status}`}
              >
                {participant.name.charAt(0).toUpperCase()}
              </div>
            ))}
            <button className="w-8 h-8 rounded-full bg-white/10 border border-dashed border-white/30 flex items-center justify-center text-gray-400 hover:border-cyan-500 hover:text-cyan-400 transition-colors">
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => startRun(run.id)}
          className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-black font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(0,255,213,0.3)] transition-all flex items-center justify-center gap-2"
        >
          <NavigateIcon className="w-4 h-4" />
          Start Run
        </button>
        <button
          onClick={() => cancelRun(run.id)}
          className="px-4 py-2 bg-white/10 text-gray-400 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-colors"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}

// Icons
function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function NavigateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

export default ScheduledRunCard;
